import React from 'react'

import { CompassIcon, XIcon } from 'lucide-react'

import { ButtonBase, Stack, Tooltip, Typography, useTheme } from '@mui/material'

import { brandTintText, dimNeutralText, navSelected } from '@repo/ui'

import { useProductTour } from './ProductTourProvider'
import { openToursDialog } from './ToursDialog'
import { useOnPageUntakenTours } from './useOnPageUntakenTours'

/**
 * How far BELOW the title's line-box centre the chip has to sit to line up with the
 * title's letterforms, in px at the header title's size.
 *
 * `alignItems: center` centres the chip on the title's LINE BOX, and a line box is not
 * where the ink is. The baseline sits at half-leading + ascent from the box top, so for
 * a font whose ascent and descent are unequal the cap band is off-centre in its own box.
 * Working that through, the line-height cancels out entirely and the offset is a pure
 * font constant:
 *
 *     offset = (ascent − descent − capHeight) / 2          [in em]
 *
 * Measured in the browser against the rendered `h5` title (canvas TextMetrics —
 * `fontBoundingBoxAscent/Descent` for the font, `actualBoundingBoxAscent` of "H" for the
 * cap height, which is string-independent unlike the ink bounds of a real title):
 * ascent 1.0em, descent 0.2083em, cap 0.723em → (1 − 0.2083 − 0.723) / 2 = 0.0343em.
 * At the title's 24px that is 0.82px, DOWN.
 *
 * The previous value was 0 — VRG-428 removed a −1.4px nudge that was derived from an
 * ASSUMED cap height and pointed the wrong way, then declined to re-derive it on the
 * grounds that the residual was sub-pixel. It was reported again, so it is corrected
 * here rather than argued away. The difference from last time is that this number is
 * measured and the formula that produced it is written down, so a change to the title's
 * size is a one-line edit and a change of font is a re-measure, not a guess.
 *
 * ## The residual this deliberately does NOT chase
 *
 * Aligning to the cap band is not the same as aligning to the ink of the actual title
 * string, and on a title with a descender the two are far apart: "Settings" measures an
 * ink centre 2.77px below the line-box centre (the `g` tail), where "Features" measures
 * 0.84px — near-identical to the cap band, because it has no descender. Chasing the ink
 * would therefore move the chip by ~2px BETWEEN PAGES, which is worse than being 2px off
 * on one of them.
 *
 * It is also not available: the chip is 30px tall inside a 31.2px line box, so it has
 * 0.6px of slack per side before it starts protruding past the title's own line box —
 * and this row's height is the header's vertical datum (see `alignSelf` below). A +2.77px
 * shift would push the chip 2.2px below the row. So the cap band is both the stable
 * target and the only one the geometry allows.
 */
const TITLE_OPTICAL_CENTRE_OFFSET_PX = 0.82

/**
 * "Take a tour" beside the page title — shown only when this page HAS a tour and
 * this user has NOT taken it.
 *
 * ## Why this shape
 *
 * It replaces an auto-starting tour. Auto-start is defensible for a single
 * onboarding tour and stops being defensible for a library: with a tour on ten
 * surfaces, a new user is interrupted ten times, each time at the moment they were
 * trying to do something. A passive invitation scales because the user chooses when.
 *
 * It is CONDITIONAL, which is what makes a page-level affordance acceptable here at
 * all. A permanent per-page help icon would be ragged (most pages have no tour) or
 * dead (an icon that opens an empty list). This one appears for the person who needs
 * it, on the surface it is about, and is gone for good once they engage — so it
 * cannot accumulate as chrome.
 *
 * Beside the TITLE, never in the header's right-hand action group: that group has a
 * strict hierarchy (one primary, rightmost; §26/§33) and an invitation is not an
 * action on the record.
 *
 * ## Dismissal semantics
 *
 * Dismissing marks the tour seen WITHOUT running it, which is the whole reason
 * `markTourSeen` is separate from `startTour`. Note the difference from the
 * auto-start path this replaces: `startTourOnce` marks seen BEFORE showing, correct
 * for a one-shot interruption ("we asked, taken or not"), but wrong for an
 * invitation — a badge that clears on RENDER would vanish for someone who never
 * looked at it. So nothing is marked until the user acts.
 *
 * Either way the tour stays reachable forever from Help → Product tours and ⌘K; this
 * only ever suppresses the unprompted nudge.
 */
export const TakeATourChip = () => {
  const theme = useTheme()
  const { startTour, markTourSeen, activeTourId, tourProgress } = useProductTour()
  const untaken = useOnPageUntakenTours()

  /*
    ONE chip whichever way, but its JOB changes with the count.

      1 tour  → a launcher. "Take a tour" / "Finish tour · 4 of 6", starts it.
      2+      → an opener. "3 tours for this page", opens the library, which already
                has an "On this page" section listing every one of them.

    The earlier version showed `untaken[0]` and silently dropped the rest, justified
    in a comment as "the author should pick" — an authoring rule enforced by a
    truncation the user cannot see, which is exactly the silent cap this codebase
    keeps deciding not to ship. Handing the plural case to the library costs no extra
    chrome and hides nothing.

    Rejected: a row of chips (the clutter this design exists to avoid) and a dropdown
    (a menu in the page header, to serve a case that should be rare by construction —
    see §48's authoring note: one tour per surface, depth lives in the library).

    The list arrives in registry order. No relevance sort, because with the plural
    case handed to the library the only thing read here is `[0]`, and `[0]` is only
    rendered when there is exactly one — which no ordering can change.
  */
  const tour = untaken[0]
  if (!tour) return null
  const multiple = untaken.length > 1

  /*
    Hidden while ANY tour is running. Caught in verification: clicking "Take a tour"
    started the tour and left the chip sitting beside the title throughout it —
    inviting the user to the thing they are currently doing, next to the spotlight
    that is doing it.

    Keyed on a tour being ACTIVE rather than on this tour being seen, because
    `startTour` deliberately does not mark seen (it is an explicit request, and the
    Help menu uses the same call to re-run a finished tour). Progress is written when
    the tour ends, so between start and end there is nothing else to key on.

    Not a `useEffect` that marks seen on start: that would make an abandoned tour
    count as taken, which is the auto-start semantics this design replaced.
  */
  if (activeTourId) return null

  // Resuming = started, walked past the first step, not finished. `hasSeenTour`
  // already excluded completed and skipped tours from `untaken`, so anything with
  // a step recorded here was left via Escape or the close button.
  const progress = tourProgress(tour.id)
  const totalSteps = tour.steps.length
  const resuming = Boolean(progress && progress.currentStep > 0)
  const stepNumber = Math.min((progress?.currentStep ?? 0) + 1, totalSteps)

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        pl: 3,
        pr: 2,
        // 2px, down from 6. The two controls inside are the chip's hit targets and both
        // measured 18px tall, under WCAG 2.5.8's 24px floor — and shortening the label
        // (below) makes the launcher's WIDTH worse too, from 79px to ~30. They now carry
        // `minHeight: 24`, which does not fit inside a 6px-padded 32px chip (content box
        // 18px). So the padding gives way rather than the target: the chip lands at 30px
        // tall, the targets at 24, and the chip is a touch smaller — which is the
        // direction this change is going anyway.
        py: 0.5,
        /*
          COLOUR comes from `ProgressPill`'s strategy; SHAPE does not. Keeping those apart is
          the whole decision here, so it is written down rather than left to be "finished" by
          the next reader.

          What was adopted: `navSelected` for the fill and `brandTintText` for the label + icon,
          replacing brand @0.08 under a FIXED `brand[600]`. Measured in the browser, that pairing
          gave 4.521:1 light and 3.464:1 dark — the dark figure under WCAG AA's 4.5:1 for body
          text — because a fixed ramp step cannot sit on a surface that moves between modes. It
          now measures 5.491:1 and 9.161:1. See `brandTintText` for the full derivation.

          What was NOT adopted: the pill's fully-round `borderRadius: 999`. This stays an 8px
          rounded rectangle, because `ProgressPill` is PASSIVE STATUS — a number that changes
          while you look at it, and nothing happens if you click it — while this is a CONTROL:
          the label launches a tour and the `x` dismisses the invitation.

          ⚠️ THE BORDER WAS REMOVED 2026-09-01, and the argument that kept it was wrong.

          An earlier version of this note claimed "the border and the squarer radius are what
          say so", and defended the border twice when the product owner questioned it. Two
          things sink that:

            1. It was not carrying the signal. `brandTintBorder` measured 1.6:1 against the
               page — a smudge. A 1px edge nobody can see is not what marks a control as
               interactive, whatever the docblock asserts.
            2. The affordance is already carried three times over, by the compass icon, the
               word "Tour", and the `x`. Those say "control" far louder than a hairline could.

          What still separates this from `ProgressPill`: the 8px radius vs fully round, an icon
          plus a label plus a dismiss control vs a bare number, and a different footprint. A
          `10/12` pill and a `Tour x` chip are not confusable without the border's help.

          Do not re-add it "for parity with a status pill" — the parity is deliberate and lives
          in the COLOUR (`navSelected` + `brandTintText`), not the chrome.
        */
        borderRadius: 2,
        bgcolor: navSelected(theme),
        // Never let an optional invitation set the header's height — the title's
        // line box is what every page's vertical rhythm is measured against.
        alignSelf: 'center',
        flexShrink: 0,
        /*
          Optical centring: `alignItems: center` puts the chip on the title's LINE BOX
          centre, and the title's letterforms are not centred in their own line box. The
          correction is DOWNWARD — see `TITLE_OPTICAL_CENTRE_OFFSET_PX` above for the
          measurement, the formula, and why the cap band rather than the string's ink is
          the thing being aligned to.

          Note this is the opposite direction from the −1.4px nudge that used to live
          here, which was derived from an assumed cap height. Both that value and this
          one are corrections to the same gap; only this one was measured.
        */
        position: 'relative',
        top: `${TITLE_OPTICAL_CENTRE_OFFSET_PX}px`,
      }}
    >
      <ButtonBase
        onClick={() => (multiple ? openToursDialog() : startTour(tour.id))}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          // WCAG 2.5.8 (24x24 minimum). Measured 18px before this; the width is carried
          // by the label + icon in every state, the height was not carried by anything.
          minHeight: 24,
          borderRadius: 1,
          // Label AND compass, in one declaration — the lucide glyph is `currentColor`, so it
          // inherits. That matters: colouring only the text would leave the icon on the old
          // fixed `brand[600]` and put two different purples 8px apart.
          color: brandTintText(theme),
          // See the `x`'s focus block below for why this is a solid `brandTintText` ring at
          // 2px rather than the `600-6%` wash used on fields. Both controls, one answer.
          '&:focus-visible': {
            boxShadow: `0 0 0 2px ${brandTintText(theme)}`,
          },
        }}
      >
        <CompassIcon strokeWidth={1} size={16} />
        {/*
          "Tour", not "Take a tour" — and not the icon alone.

          Icon-only was the other option offered and it loses on three counts. The
          compass is not a conventional glyph for "guided tour" the way `?` is for help,
          so it carries no meaning to someone who has never taken one — which is exactly
          and only who this chip is shown to. A tooltip does not recover that on touch.
          And it would sit immediately beside an unlabelled `x`: two bare glyphs, one of
          which is destructive-ish, with nothing to tell them apart at a glance.

          The deciding argument is that icon-only is not a smaller version of this
          control, it is a lossier one: the resuming state's whole job is to say "4 of 6"
          (see below), and there is nowhere for that to live without words. Keeping one
          word keeps all three states expressible in one shape.

          The resuming and plural labels are deliberately NOT shortened alongside it.
          They are informational rather than invitational, they are only reachable after
          the user has already engaged once, and the resting label is the one that was
          reported as too loud.
        */}
        {/*
          "Finish tour · 4 of 6" once they have started one, "Tour" otherwise.
          The label has to match what the click DOES — clicking resumes at the saved
          step, so offering "Tour" to someone four steps in would misdescribe
          it in both directions: it reads as starting over, and it hides the fact
          that they are nearly done, which is the one thing likely to bring them
          back.

          Only reachable because Escape and the close button now record `exit`
          rather than `dismissed`. A tour left via "Skip tour" is terminal and never
          reaches this component at all.
        */}
        <Typography variant="body4" component="span" sx={{ fontWeight: 600 }}>
          {multiple
            ? `${untaken.length} tours for this page`
            : resuming
              ? `Finish tour · ${stepNumber} of ${totalSteps}`
              : 'Tour'}
        </Typography>
      </ButtonBase>

      {/* X, not a trash glyph: this removes the invitation, it does not destroy the
          tour — which stays in the library (§38). No confirmation, because it is
          cheap to undo by opening Help → Product tours.

          In PLURAL it dismisses every tour the chip is currently offering, and the
          tooltip says so. Dismissing only the first would leave the chip in place
          reading "2 tours for this page", so the gesture would appear to do nothing
          — the worst possible outcome for a control whose whole purpose is "stop
          asking me". Per-tour control stays in the library, where each row is
          individually addressable. */}
      <Tooltip
        title={
          multiple
            ? "Don't offer tours on this page"
            : resuming
              ? "Don't remind me about this tour"
              : "Don't show this again"
        }
        placement="top"
      >
        <ButtonBase
          aria-label="Dismiss tour invitation"
          onClick={() => untaken.forEach(t => markTourSeen(t.id))}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 1,
            // WCAG 2.5.8. A 14px glyph at `p: 0.5` measured 18x18; the minimums grow the
            // hit area without growing the glyph, so nothing about it reads louder.
            minWidth: 24,
            minHeight: 24,
            /*
              `dimNeutralText`, not `text.secondary` — re-checked against the STRONGER fill and
              it was already failing before it.

              `text.secondary` is `#a1a1aa` in BOTH modes, which is the exact trap that helper
              was written for. Measured on the LIGHT fill it is 2.149:1 — under even WCAG
              1.4.11's 3:1 non-text floor, and it was already 2.149 against the old @0.08 fill,
              so this glyph has been failing since it was written and the fill change neither
              caused it nor materially moved it. (Dark was fine both before and after: 7.287
              then 6.319, because a pale grey on a near-black chip was never the risk.)
              `dimNeutralText` swaps to `#52525b` in light only — 6.197:1 there, 6.319:1 dark —
              and stays visibly the quieter of the chip's two controls.
            */
            color: dimNeutralText(theme),
            '&:hover': { color: theme.palette.text.primary },
            /*
              A SOLID `brandTintText` ring, not the `600-6%` wash — and this is a real change,
              not a tidy-up.

              `600-6%` is the §31 FIELD treatment, where it is the second cue: a field also
              swaps its border to `brand[600]` on focus and the ring merely softens that edge.
              Here there is no border to swap, so the ring is the entire focus affordance — and
              6% brand over an already-brand-tinted chip is a ~5/255 per-channel step. Measured
              against its own background: 1.078:1 light, 1.053:1 dark. That is not a weak cue,
              it is no cue.

              Raising the alpha does not rescue it, which is why the hue had to change too.
              Measured on the same fill, brand[600] tops out at 4.323:1 light and 3.004:1 dark
              at FULL opacity — so on the dark chip no alpha of `brand[600]` clears 1.4.11's 3:1
              with any margin at all, and `600-32%` reaches only 1.529 / 1.372. `brandTintText`
              is the one brand value that does: 5.491:1 light, 9.161:1 dark. It stays inside the
              design system's brand-ring convention while actually being visible, which is the
              whole point of the convention.

              2px, not 3px: the button is 24px inside a 30px chip, so there are 3px of slack per
              side and a 3px ring would sit on the chip's own border.
            */
            '&:focus-visible': {
              boxShadow: `0 0 0 2px ${brandTintText(theme)}`,
            },
          }}
        >
          <XIcon strokeWidth={1} size={14} />
        </ButtonBase>
      </Tooltip>
    </Stack>
  )
}

